import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import TextInput from '../ui/TextInput';
import { Select } from '../ui/Select';
import { RadioGroup } from '../ui/RadioGroup';
import { Label } from '../ui/Label';
import { DateInput } from '../ui';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import {
  XIcon,
  TrashIcon,
  PencilIcon,
  PaintBrushIcon,
} from '../ui/Icons';
import useSWR from 'swr';
import {
  getConditionalFormattingRules,
  createConditionalFormattingRule,
  updateConditionalFormattingRule,
  deleteConditionalFormattingRule,
  setDefaultConditionalFormattingRule,
  ConditionalFormattingRule as APIConditionalFormattingRule,
  CreateConditionalFormattingRuleDto,
} from '../../services/conditionalFormattingService';
import { useNotification } from '../../contexts/NotificationContext';
import { validateExpression } from '../../utils/expressionEvaluator';
import { useApiOptions } from '../../hooks/useApiOptions';

interface ConditionalFormattingRule extends APIConditionalFormattingRule {}

interface ConditionalFormattingManagerProps {
  moduleName: string;
  entityName?: string;
  availableColumns: Array<{ 
    key: string; 
    label: string; 
    type?: string; 
    options?: any[];
    isForeignKey?: boolean;
    foreignKeyField?: string;
    apiEndpoint?: string; // API endpoint to fetch options for foreign key/select fields
    labelField?: string; // Field name in API response to use as label
  }>;
  tableData?: any[]; // Table data to extract foreign key values from
  isOpen: boolean;
  onClose: () => void;
  onRulesChange?: () => void;
  position?: { top: number; right: number } | null;
}

const ConditionalFormattingManager: React.FC<ConditionalFormattingManagerProps> = ({
  moduleName,
  entityName = 'Table',
  availableColumns = [],
  tableData = [],
  isOpen,
  onClose,
  onRulesChange,
  position,
}) => {
  const { colors } = useTheme();
  const { showNotification } = useNotification();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ConditionalFormattingRule | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<ConditionalFormattingRule | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Form state
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [conditionMode, setConditionMode] = useState<'column' | 'expression'>('column');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [expression, setExpression] = useState('');
  const [operator, setOperator] = useState('greaterThan');
  const [value, setValue] = useState('');
  const [value2, setValue2] = useState('');
  const [scope, setScope] = useState<'column' | 'row'>('column');
  const [backgroundColor, setBackgroundColor] = useState('bg-green-100');
  const [textColor, setTextColor] = useState('text-green-800');
  const [fontWeight, setFontWeight] = useState('');
  const [priority, setPriority] = useState(0);
  const [showExpressionHelper, setShowExpressionHelper] = useState(false);

  // Load rules from API using SWR
  const { data: rules = [], mutate: mutateRules } = useSWR<ConditionalFormattingRule[]>(
    isOpen ? `conditional-formatting-${moduleName}` : null,
    () => getConditionalFormattingRules(moduleName),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Collect API endpoints from availableColumns for dynamic option fetching
  // Use selectedColumn as the key to ensure SWR refetches when column changes
  const apiEndpoints = useMemo(() => {
    const endpoints: Record<string, { endpoint: string; labelField?: string }> = {};
    if (selectedColumn && showSaveModal) {
      const selectedCol = availableColumns.find(col => col.key === selectedColumn);
      if (selectedCol?.apiEndpoint) {
        // Use selectedColumn as the key - this ensures SWR cache key changes when column changes
        // The key format in useApiOptions will be: `api-options-${selectedColumn}-${endpoint}`
        // So changing selectedColumn will create a new cache key and trigger a refetch
        // IMPORTANT: Use a unique key that includes both column and endpoint to avoid cache conflicts
        const uniqueKey = `${selectedColumn}-${selectedCol.apiEndpoint}`;
        endpoints[uniqueKey] = {
          endpoint: selectedCol.apiEndpoint,
          labelField: selectedCol.labelField,
        };
      }
    }
    return endpoints;
  }, [availableColumns, selectedColumn, showSaveModal]);

  // Fetch options from API endpoints using custom hook
  const apiOptionsData = useApiOptions(apiEndpoints);
  
  // Get the correct key to access API data (matches the uniqueKey used in apiEndpoints)
  const apiDataKey = useMemo(() => {
    if (selectedColumn && showSaveModal) {
      const selectedCol = availableColumns.find(col => col.key === selectedColumn);
      if (selectedCol?.apiEndpoint) {
        return `${selectedColumn}-${selectedCol.apiEndpoint}`;
      }
    }
    return '';
  }, [availableColumns, selectedColumn, showSaveModal]);

  // Force clear value when column changes to ensure fresh data is used
  useEffect(() => {
    if (showSaveModal && !editingRule && selectedColumn) {
      // Clear values when column changes to force using new API data
      setValue('');
      setValue2('');
    }
  }, [selectedColumn, showSaveModal, editingRule]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSaveModal || showDeleteConfirm) {
        return;
      }
      
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('[role="dialog"]') && !target.closest('.fixed.inset-0')) {
          onClose();
        }
      }
    };

    if (isOpen && !showSaveModal && !showDeleteConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, showSaveModal, showDeleteConfirm]);

  // Reset form when opening modal
  useEffect(() => {
    if (showSaveModal && !editingRule) {
      resetForm();
    }
  }, [showSaveModal, editingRule]);

  // Clear value when column changes or mode changes
  useEffect(() => {
    if (showSaveModal && !editingRule) {
      setValue('');
      setValue2('');
    }
  }, [selectedColumn, conditionMode, showSaveModal, editingRule]);

  const resetForm = () => {
    setRuleName('');
    setRuleDescription('');
    setConditionMode('column');
    setSelectedColumn('');
    setExpression('');
    setOperator('greaterThan');
    setValue('');
    setValue2('');
    setScope('column');
    setBackgroundColor('bg-green-100');
    setTextColor('text-green-800');
    setFontWeight('');
    setPriority(0);
  };

  const loadRuleForEditing = (rule: ConditionalFormattingRule) => {
    setRuleName(rule.name);
    setRuleDescription(rule.description || '');
    setConditionMode(rule.condition.type);
    setSelectedColumn(rule.condition.columnKey || rule.columnKey);
    setExpression(rule.condition.expression || '');
    setOperator(rule.condition.operator);
    setValue(String(rule.condition.value || ''));
    setValue2(String(rule.condition.value2 || ''));
    setScope(rule.scope);
    setBackgroundColor(rule.style.backgroundColor || 'bg-green-100');
    setTextColor(rule.style.textColor || 'text-green-800');
    setFontWeight(rule.style.fontWeight || '');
    setPriority(rule.priority);
  };

  const handleSaveRule = async () => {
    if (!ruleName.trim()) {
      showNotification('Rule name is required', 'error');
      return;
    }

    if (conditionMode === 'column' && !selectedColumn) {
      showNotification('Please select a column', 'error');
      return;
    }

    if (conditionMode === 'expression' && !expression.trim()) {
      showNotification('Please enter an expression', 'error');
      return;
    }

    if (conditionMode === 'expression') {
      const columnKeys = availableColumns.map(col => col.key);
      const validation = validateExpression(expression, columnKeys);
      if (!validation.valid) {
        showNotification(validation.error || 'Invalid expression', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      const ruleData: CreateConditionalFormattingRuleDto = {
        moduleName,
        columnKey: conditionMode === 'column' ? selectedColumn : (selectedColumn || availableColumns[0]?.key || ''),
        name: ruleName.trim(),
        description: ruleDescription.trim() || undefined,
        scope,
        condition: {
          type: conditionMode,
          ...(conditionMode === 'column' ? { columnKey: selectedColumn } : { expression: expression.trim() }),
          operator,
          value: (() => {
            if (conditionMode === 'expression') {
              // For expressions, always parse as number
              return isNaN(parseFloat(value)) ? value : parseFloat(value);
            }
            // For column mode, check column type
            const selectedCol = availableColumns.find(col => col.key === selectedColumn);
            const columnType = selectedCol?.type || 'text';
            if (columnType === 'number') {
              return isNaN(parseFloat(value)) ? value : parseFloat(value);
            }
            if (columnType === 'date') {
              return value; // Keep as string for dates
            }
            return value; // Keep as string for text/select/boolean
          })(),
          ...(operator === 'between' ? { 
            value2: (() => {
              if (conditionMode === 'expression') {
                return isNaN(parseFloat(value2)) ? value2 : parseFloat(value2);
              }
              const selectedCol = availableColumns.find(col => col.key === selectedColumn);
              const columnType = selectedCol?.type || 'text';
              if (columnType === 'number') {
                return isNaN(parseFloat(value2)) ? value2 : parseFloat(value2);
              }
              if (columnType === 'date') {
                return value2;
              }
              return value2;
            })()
          } : {}),
        },
        style: {
          backgroundColor,
          textColor,
          ...(fontWeight ? { fontWeight } : {}),
        },
        isActive: true,
        priority,
        isDefault: false,
      };

      if (editingRule) {
        await updateConditionalFormattingRule(editingRule.id, ruleData, moduleName);
        showNotification('Conditional formatting rule updated successfully', 'success');
      } else {
        await createConditionalFormattingRule(ruleData);
        showNotification('Conditional formatting rule created successfully', 'success');
      }

      await mutateRules();
      if (onRulesChange) {
        onRulesChange();
      }

      setShowSaveModal(false);
      resetForm();
      setEditingRule(null);
    } catch (error: any) {
      console.error('Error saving conditional formatting rule:', error);
      showNotification(
        error.response?.data?.message || 'Failed to save conditional formatting rule',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;

    setIsLoading(true);
    try {
      await deleteConditionalFormattingRule(ruleToDelete.id, moduleName);
      showNotification('Conditional formatting rule deleted successfully', 'success');
      
      await mutateRules();
      if (onRulesChange) {
        onRulesChange();
      }

      setShowDeleteConfirm(false);
      setRuleToDelete(null);
    } catch (error: any) {
      console.error('Error deleting conditional formatting rule:', error);
      showNotification(
        error.response?.data?.message || 'Failed to delete conditional formatting rule',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetAsDefault = async (ruleId: number) => {
    setIsLoading(true);
    try {
      await setDefaultConditionalFormattingRule(ruleId);
      showNotification('Default rule updated successfully', 'success');
      
      await mutateRules();
      if (onRulesChange) {
        onRulesChange();
      }
    } catch (error: any) {
      console.error('Error setting default rule:', error);
      showNotification(
        error.response?.data?.message || 'Failed to set default rule',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const startEditRule = (rule: ConditionalFormattingRule) => {
    setEditingRule(rule);
    loadRuleForEditing(rule);
    setShowSaveModal(true);
  };

  const confirmDeleteRule = (rule: ConditionalFormattingRule) => {
    setRuleToDelete(rule);
    setShowDeleteConfirm(true);
  };

  const operatorMap: Record<string, string> = {
    greaterThan: '>',
    greaterThanOrEqual: '>=',
    lessThan: '<',
    lessThanOrEqual: '<=',
    equals: '=',
    notEquals: '!=',
    between: 'between',
    contains: 'contains',
    isEmpty: 'is empty',
    isNotEmpty: 'is not empty',
  };

  const colorOptions = [
    { bg: 'bg-green-100', text: 'text-green-800', label: 'Green' },
    { bg: 'bg-red-100', text: 'text-red-800', label: 'Red' },
    { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Yellow' },
    { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Blue' },
    { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Purple' },
    { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Orange' },
  ];

  if (!isOpen) return null;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[90]" onClick={onClose} />
      )}
      <div
        ref={containerRef}
        data-conditional-formatting-manager
        className={cn(
          'fixed min-w-[500px] max-w-[600px] z-[100] backdrop-blur-md rounded-xl shadow-xl border',
          colors.background,
          colors.border
        )}
        style={position ? {
          top: `${position.top}px`,
          right: `${position.right}px`,
        } : undefined}
      >
        {/* Header */}
        <div className={cn('flex items-center justify-between p-4 border-b', colors.border)}>
          <h3 className={cn('text-sm font-semibold flex items-center', colors.text)}>
            <PaintBrushIcon className={cn('w-4 h-4 mr-2', colors.textSecondary)} />
            {entityName} Conditional Formatting
          </h3>
          <button
            onClick={onClose}
            className={cn(
              'h-8 w-8 p-0 rounded-lg transition-colors',
              colors.textSecondary,
              `hover:${colors.surfaceHover} hover:${colors.text}`
            )}
            title="Close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 max-h-[600px] overflow-y-auto">
          {/* Quick Actions */}
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                resetForm();
                setEditingRule(null);
                setShowSaveModal(true);
              }}
              className="text-xs"
            >
              + Create New Rule
            </Button>
          </div>

          {/* Rules List */}
          <div className="space-y-2">
            {rules.length === 0 ? (
              <div className={cn('text-center py-8', colors.textMuted)}>
                <PaintBrushIcon className={cn('w-8 h-8 mx-auto mb-2 opacity-50', colors.textSecondary)} />
                <p className={cn('text-sm font-medium', colors.text)}>No conditional formatting rules</p>
                <p className={cn('text-xs mt-1', colors.textMuted)}>
                  Click "Create New Rule" to add your first rule
                </p>
              </div>
            ) : (
              rules.map((rule) => {
                const isDefault = rule.isDefault;
                return (
                  <div
                    key={rule.id}
                    className={cn(
                      'p-4 border rounded-xl transition-all duration-200 shadow-sm hover:shadow-md',
                      isDefault
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-300 dark:border-green-700'
                        : `${colors.surface} ${colors.border} hover:${colors.surfaceHover}`
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4
                            className={cn(
                              'text-sm font-semibold truncate',
                              isDefault ? 'text-green-800 dark:text-green-200' : colors.text
                            )}
                          >
                            {rule.name}
                          </h4>
                          {isDefault && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200 border border-green-300 dark:border-green-700">
                              Default
                            </span>
                          )}
                          {!rule.isActive && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                              Inactive
                            </span>
                          )}
                        </div>

                        {rule.description && (
                          <p
                            className={cn(
                              'text-xs mt-1 truncate',
                              isDefault ? 'text-green-700 dark:text-green-300' : colors.textSecondary
                            )}
                          >
                            {rule.description}
                          </p>
                        )}

                        <div
                          className={cn(
                            'text-xs mt-2 flex items-center gap-2',
                            isDefault ? 'text-green-600 dark:text-green-400' : colors.textMuted
                          )}
                        >
                          <span>
                            {rule.condition.type === 'column' 
                              ? `Column: ${rule.condition.columnKey || rule.columnKey}`
                              : `Expression: ${rule.condition.expression}`
                            }
                          </span>
                          <span>•</span>
                          <span>{operatorMap[rule.condition.operator]} {rule.condition.value}</span>
                          <span>•</span>
                          <span>Scope: {rule.scope}</span>
                        </div>

                        {/* Style Preview */}
                        <div className="mt-2">
                          <span
                            className={cn(
                              'inline-block px-2 py-1 text-xs rounded',
                              rule.style.backgroundColor || 'bg-gray-100',
                              rule.style.textColor || 'text-gray-800',
                              rule.style.fontWeight || ''
                            )}
                          >
                            Preview
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 ml-2">
                        {!isDefault ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetAsDefault(rule.id)}
                            disabled={isLoading}
                            className={cn(
                              'h-6 px-2 text-xs',
                              'text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                            )}
                            title="Set as default rule"
                          >
                            ⭐ Default
                          </Button>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditRule(rule)}
                          className={cn(
                            'h-6 px-2 text-xs',
                            isDefault
                              ? 'text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-800/30'
                              : colors.text
                          )}
                        >
                          <PencilIcon className="w-3 h-3" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDeleteRule(rule)}
                          className={cn(
                            'h-6 px-2 text-xs',
                            isDefault
                              ? 'text-red-700 hover:text-red-900 hover:bg-red-100 dark:text-red-300 dark:hover:text-red-100 dark:hover:bg-red-800/30'
                              : 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                          )}
                          title="Delete rule"
                        >
                          <TrashIcon className="w-3 h-3" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Save Rule Modal */}
      {showSaveModal && (
        <Modal isOpen={showSaveModal} onClose={() => setShowSaveModal(false)} size="lg">
          <ModalHeader>
            <ModalTitle>{editingRule ? 'Edit Conditional Formatting Rule' : 'Create Conditional Formatting Rule'}</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              {/* Rule Name */}
              <div>
                <Label required>
                  Rule Name
                </Label>
                <TextInput
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="e.g., High Amount Invoices"
                  name="ruleName"
                />
              </div>

              {/* Description */}
              <div>
                <Label>
                  Description (optional)
                </Label>
                <TextInput
                  value={ruleDescription}
                  onChange={(e) => setRuleDescription(e.target.value)}
                  placeholder="e.g., Highlights invoices over €1000"
                  name="ruleDescription"
                />
              </div>

              {/* Condition Type */}
              <div>
                <Label className="mb-2">
                  Condition Type
                </Label>
                <RadioGroup
                  options={[
                    { value: 'column', label: 'Single Column Value' },
                    { value: 'expression', label: 'Expression Calculation' }
                  ]}
                  value={conditionMode}
                  onChange={(val) => setConditionMode(val as 'column' | 'expression')}
                  name="conditionMode"
                  orientation="horizontal"
                />
              </div>

              {/* Column Condition */}
              {conditionMode === 'column' && (
                <div>
                  <Label required>
                    Column
                  </Label>
                  <Select
                    value={selectedColumn}
                    onChange={(e) => setSelectedColumn(e.target.value)}
                    placeholder="Select a column..."
                    name="selectedColumn"
                    options={availableColumns.map((col) => ({ value: col.key, label: col.label }))}
                  />
                </div>
              )}

              {/* Expression Condition */}
              {conditionMode === 'expression' && (
                <div>
                  <Label required>
                    Expression
                  </Label>
                  <div className="flex gap-2">
                    <TextInput
                      value={expression}
                      onChange={(e) => setExpression(e.target.value)}
                      placeholder="e.g., invoiceAmount - paymentTotal"
                      name="expression"
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowExpressionHelper(!showExpressionHelper)}
                      type="button"
                    >
                      ?
                    </Button>
                  </div>
                  {showExpressionHelper && (
                    <div className={cn('mt-2 p-3 rounded border text-xs', colors.surface, colors.border)}>
                      <div className="mb-2">
                        <strong>Available Columns:</strong>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {availableColumns.map((col) => (
                            <button
                              key={col.key}
                              type="button"
                              onClick={() => setExpression(expression + col.key)}
                              className={cn('px-2 py-1 rounded text-xs', colors.surfaceHover, colors.border)}
                            >
                              {col.key}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mb-2">
                        <strong>Operators:</strong> <code>+</code> <code>-</code> <code>*</code> <code>/</code> <code>%</code> <code>(</code> <code>)</code>
                      </div>
                      <div>
                        <strong>Examples:</strong>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li><code>invoiceAmount - paymentTotal</code></li>
                          <li><code>(invoiceAmount - invoiceDiscount) / invoiceAmount * 100</code></li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Operator */}
              <div>
                <Label required>
                  Operator
                </Label>
                <Select
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                  placeholder="Select an operator..."
                  name="operator"
                  options={[
                    { value: 'greaterThan', label: 'Greater Than' },
                    { value: 'greaterThanOrEqual', label: 'Greater Than Or Equal' },
                    { value: 'lessThan', label: 'Less Than' },
                    { value: 'lessThanOrEqual', label: 'Less Than Or Equal' },
                    { value: 'equals', label: 'Equals' },
                    { value: 'notEquals', label: 'Not Equals' },
                    { value: 'between', label: 'Between' },
                    { value: 'contains', label: 'Contains' },
                    { value: 'isEmpty', label: 'Is Empty' },
                    { value: 'isNotEmpty', label: 'Is Not Empty' },
                  ]}
                />
              </div>

              {/* Value - Dynamic based on column type */}
              {!['isEmpty', 'isNotEmpty'].includes(operator) && (
                <>
                  <div>
                    <Label required>
                      {operator === 'between' ? 'Min Value' : 'Value'}
                    </Label>
                    {(() => {
                      const selectedCol = availableColumns.find(col => col.key === selectedColumn);
                      const columnType = conditionMode === 'column' ? (selectedCol?.type || 'text') : 'number';
                      const columnOptions = selectedCol?.options || [];
                      const isForeignKey = selectedCol?.isForeignKey || false;
                      const apiEndpoint = selectedCol?.apiEndpoint;
                      const labelField = selectedCol?.labelField;

                      // Get API-fetched options if available
                      // When selectedColumn changes, apiOptionsData[apiDataKey] will be refetched automatically
                      // Only use API data if the key exactly matches to avoid stale data from previous columns
                      let apiFetchedOptions: Array<{ value: string; label: string }> = [];
                      const expectedKey = apiEndpoint ? `${selectedColumn}-${apiEndpoint}` : '';
                      // CRITICAL: Only use API data if:
                      // 1. We have an apiEndpoint
                      // 2. The expectedKey matches the apiDataKey (ensures it's for the current column)
                      // 3. The data exists and is not empty
                      // 4. The apiDataKey is not empty (ensures we're not using stale data from a previous column)
                      const hasValidApiData = apiEndpoint && expectedKey && apiDataKey === expectedKey && apiDataKey !== '' && apiOptionsData[apiDataKey] && apiOptionsData[apiDataKey].length > 0;
                      if (hasValidApiData) {
                        const apiData = apiOptionsData[apiDataKey];
                        const effectiveLabelField = labelField; // Use only the configured labelField
                        apiFetchedOptions = apiData.map((item: any) => {
                          // Use only the configured labelField, no fallback
                          const label = effectiveLabelField && item[effectiveLabelField] !== undefined && item[effectiveLabelField] !== null
                            ? item[effectiveLabelField]
                            : `Item #${item.id}`;
                          return {
                            value: String(item.id),
                            label: String(label),
                          };
                        }).sort((a: { value: string; label: string }, b: { value: string; label: string }) => a.label.localeCompare(b.label));
                      }

                      // Extract foreign key values from table data (fallback if no API options)
                      // Only use table data if we don't have API data for the current column
                      let foreignKeyOptions: Array<{ value: string; label: string }> = [];
                      if (isForeignKey && !hasValidApiData && apiFetchedOptions.length === 0 && columnOptions.length === 0 && tableData && tableData.length > 0) {
                        const uniqueValues = new Map<string, string>();
                        tableData.forEach((row: any) => {
                          // Handle nested keys like 'client.clientName'
                          if (selectedColumn.includes('.')) {
                            const parts = selectedColumn.split('.');
                            let value = row;
                            for (const part of parts) {
                              value = value?.[part];
                            }
                            if (value !== null && value !== undefined && value !== '') {
                              const key = String(value);
                              // For nested keys, use the value as both key and label
                              if (!uniqueValues.has(key)) {
                                uniqueValues.set(key, key);
                              }
                            }
                          } else {
                            // Handle direct foreign key column (e.g., 'clientId')
                            const fkField = selectedCol?.foreignKeyField || selectedColumn;
                            const fkValue = row[fkField];
                            if (fkValue !== null && fkValue !== undefined) {
                              const key = String(fkValue);
                              // Try to get display value from related entity
                              let displayValue = key;
                              // Try to find related entity (e.g., row.client?.clientName for clientId)
                              const baseKey = fkField.replace('Id', '');
                              const relatedEntity = row[baseKey];
                              if (relatedEntity) {
                                // Try common display fields
                                displayValue = relatedEntity[`${baseKey}Name`] || 
                                              relatedEntity.name || 
                                              relatedEntity.label ||
                                              relatedEntity.description ||
                                              key;
                              }
                              if (!uniqueValues.has(key)) {
                                uniqueValues.set(key, displayValue);
                              }
                            }
                          }
                        });
                        foreignKeyOptions = Array.from(uniqueValues.entries()).map(([val, label]) => ({
                          value: val,
                          label: String(label),
                        })).sort((a, b) => a.label.localeCompare(b.label));
                      }

                      // For expression mode, always use number input
                      if (conditionMode === 'expression') {
                        return (
                          <TextInput
                            type="number"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="0"
                            name="value"
                          />
                        );
                      }

                      // For foreign keys, always show select dropdown
                      if (isForeignKey) {
                        // Priority: 1. apiFetchedOptions (from API, for current column - only if valid), 2. foreignKeyOptions (from tableData)
                        // NEVER use columnOptions if we have an API endpoint - it may contain stale data from other columns
                        // Only show options that are valid for the current column
                        let optionsToUse: Array<{ value: string; label: string }> = [];
                        
                        if (apiEndpoint) {
                          // If we have an API endpoint, ONLY use API data (never columnOptions or foreignKeyOptions)
                          // This ensures we don't show stale data from previous columns
                          if (hasValidApiData && apiFetchedOptions.length > 0) {
                            optionsToUse = apiFetchedOptions;
                          }
                          // Otherwise, show empty (will show "Loading options..." placeholder)
                        } else {
                          // If no API endpoint, use foreignKeyOptions or columnOptions as fallback
                          optionsToUse = foreignKeyOptions.length > 0
                            ? foreignKeyOptions
                            : columnOptions;
                        }
                        
                        const valueOptions = optionsToUse.length > 0
                          ? optionsToUse
                              .filter((option: any) => {
                                const optionValue = typeof option === 'string' ? option : option.value || option.label;
                                return optionValue !== undefined && optionValue !== null && optionValue !== '';
                              })
                              .map((option: any) => {
                                const optionValue = typeof option === 'string' ? option : option.value || option.label;
                                const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                                return { value: String(optionValue), label: String(optionLabel) };
                              })
                          : [];
                        return (
                          <Select
                            key={`value-select-${selectedColumn}`}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={optionsToUse.length > 0 ? "Select a value..." : (apiEndpoint ? "Loading options..." : "Select a value...")}
                            name="value"
                            options={valueOptions}
                          />
                        );
                      }

                      // For column mode, use the column's type
                      switch (columnType) {
                        case 'number':
                          return (
                            <TextInput
                              type="number"
                              value={value}
                              onChange={(e) => setValue(e.target.value)}
                              placeholder="0"
                              name="value"
                            />
                          );
                        case 'date':
                          return (
                            <DateInput
                              value={value}
                              onChange={(e) => setValue(e.target.value)}
                              name="value"
                            />
                          );
                        case 'select':
                        case 'boolean':
                          // Use column options
                          const colOpts = columnOptions
                            .filter((option: any) => {
                              const optionValue = typeof option === 'string' ? option : option.value || option.label;
                              return optionValue !== undefined && optionValue !== null && optionValue !== '';
                            })
                            .map((option: any) => {
                              const optionValue = typeof option === 'string' ? option : option.value || option.label;
                              const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                              return { value: String(optionValue), label: String(optionLabel) };
                            });
                          return (
                            <Select
                              value={value}
                              onChange={(e) => setValue(e.target.value)}
                              placeholder="Select a value..."
                              name="value"
                              options={colOpts}
                            />
                          );
                        default:
                          return (
                            <TextInput
                              type="text"
                              value={value}
                              onChange={(e) => setValue(e.target.value)}
                              placeholder="Enter value"
                              name="value"
                            />
                          );
                      }
                    })()}
                  </div>

                  {/* Value 2 (for between) */}
                  {operator === 'between' && (
                    <div>
                      <Label required>
                        Max Value
                      </Label>
                      {(() => {
                        const selectedCol = availableColumns.find(col => col.key === selectedColumn);
                        const columnType = conditionMode === 'column' ? (selectedCol?.type || 'text') : 'number';
                        const columnOptions = selectedCol?.options || [];
                        const isForeignKey = selectedCol?.isForeignKey || false;
                        const apiEndpoint = selectedCol?.apiEndpoint;
                        const labelField = selectedCol?.labelField;

                        // Get API-fetched options if available
                        // When selectedColumn changes, apiOptionsData[apiDataKey] will be refetched automatically
                        // Only use API data if the key exactly matches to avoid stale data from previous columns
                        let apiFetchedOptions: Array<{ value: string; label: string }> = [];
                        const expectedKey = apiEndpoint ? `${selectedColumn}-${apiEndpoint}` : '';
                        // CRITICAL: Only use API data if:
                        // 1. We have an apiEndpoint
                        // 2. The expectedKey matches the apiDataKey (ensures it's for the current column)
                        // 3. The data exists and is not empty
                        // 4. The apiDataKey is not empty (ensures we're not using stale data from a previous column)
                        const hasValidApiData = apiEndpoint && expectedKey && apiDataKey === expectedKey && apiDataKey !== '' && apiOptionsData[apiDataKey] && apiOptionsData[apiDataKey].length > 0;
                        if (hasValidApiData) {
                          const apiData = apiOptionsData[apiDataKey];
                          const effectiveLabelField = labelField; // Use only the configured labelField
                          apiFetchedOptions = apiData.map((item: any) => {
                            // Use only the configured labelField, no fallback
                            const label = effectiveLabelField && item[effectiveLabelField] !== undefined && item[effectiveLabelField] !== null
                              ? item[effectiveLabelField]
                              : `Item #${item.id}`;
                            return {
                              value: String(item.id),
                              label: String(label),
                            };
                          }).sort((a: { value: string; label: string }, b: { value: string; label: string }) => a.label.localeCompare(b.label));
                        }

                        // Extract foreign key values from table data (fallback if no API options)
                        // Only use table data if we don't have API data for the current column
                        let foreignKeyOptions: Array<{ value: string; label: string }> = [];
                        if (isForeignKey && !hasValidApiData && apiFetchedOptions.length === 0 && columnOptions.length === 0 && tableData && tableData.length > 0) {
                          const uniqueValues = new Map<string, string>();
                          tableData.forEach((row: any) => {
                            if (selectedColumn.includes('.')) {
                              const parts = selectedColumn.split('.');
                              let value = row;
                              for (const part of parts) {
                                value = value?.[part];
                              }
                              if (value !== null && value !== undefined && value !== '') {
                                const key = String(value);
                                if (!uniqueValues.has(key)) {
                                  uniqueValues.set(key, key);
                                }
                              }
                            } else {
                              const fkField = selectedCol?.foreignKeyField || selectedColumn;
                              const fkValue = row[fkField];
                              if (fkValue !== null && fkValue !== undefined) {
                                const key = String(fkValue);
                                let displayValue = key;
                                const baseKey = fkField.replace('Id', '');
                                const relatedEntity = row[baseKey];
                                if (relatedEntity) {
                                  displayValue = relatedEntity[`${baseKey}Name`] || 
                                                relatedEntity.name || 
                                                relatedEntity.label ||
                                                relatedEntity.description ||
                                                key;
                                }
                                if (!uniqueValues.has(key)) {
                                  uniqueValues.set(key, displayValue);
                                }
                              }
                            }
                          });
                          foreignKeyOptions = Array.from(uniqueValues.entries()).map(([val, label]) => ({
                            value: val,
                            label: String(label),
                          })).sort((a, b) => a.label.localeCompare(b.label));
                        }

                        // For expression mode, always use number input
                        if (conditionMode === 'expression') {
                          return (
                            <TextInput
                              type="number"
                              value={value2}
                              onChange={(e) => setValue2(e.target.value)}
                              placeholder="0"
                              name="value2"
                            />
                          );
                        }

                        // For foreign keys, always show select dropdown
                        if (isForeignKey) {
                          // Priority: 1. apiFetchedOptions (from API, for current column - only if valid), 2. foreignKeyOptions (from tableData)
                          // NEVER use columnOptions if we have an API endpoint - it may contain stale data from other columns
                          // Only show options that are valid for the current column
                          let optionsToUse: Array<{ value: string; label: string }> = [];
                          
                          if (apiEndpoint) {
                            // If we have an API endpoint, ONLY use API data (never columnOptions or foreignKeyOptions)
                            // This ensures we don't show stale data from previous columns
                            if (hasValidApiData && apiFetchedOptions.length > 0) {
                              optionsToUse = apiFetchedOptions;
                            }
                            // Otherwise, show empty (will show "Loading options..." placeholder)
                          } else {
                            // If no API endpoint, use foreignKeyOptions or columnOptions as fallback
                            optionsToUse = foreignKeyOptions.length > 0
                              ? foreignKeyOptions
                              : columnOptions;
                          }
                          
                          const value2Opts = optionsToUse.length > 0
                            ? optionsToUse.map((option: any) => {
                                const optionValue = typeof option === 'string' ? option : option.value || option.label;
                                const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                                return { value: String(optionValue), label: String(optionLabel) };
                              })
                            : [];
                          return (
                            <Select
                              key={`value2-select-${selectedColumn}`}
                              value={value2}
                              onChange={(e) => setValue2(e.target.value)}
                              placeholder={optionsToUse.length > 0 ? "Select a value..." : (apiEndpoint ? "Loading options..." : "Select a value...")}
                              name="value2"
                              options={value2Opts}
                            />
                          );
                        }

                        // For column mode, use the column's type
                        switch (columnType) {
                          case 'number':
                            return (
                              <TextInput
                                type="number"
                                value={value2}
                                onChange={(e) => setValue2(e.target.value)}
                                placeholder="0"
                                name="value2"
                              />
                            );
                          case 'date':
                            return (
                              <DateInput
                                value={value2}
                                onChange={(e) => setValue2(e.target.value)}
                                name="value2"
                              />
                            );
                          case 'select':
                          case 'boolean': {
                            const optionsToUseV2 = selectedCol?.options || [];
                            const value2SelectOpts = optionsToUseV2
                              .filter((option: any) => {
                                const optionValue = typeof option === 'string' ? option : option.value || option.label;
                                return optionValue !== undefined && optionValue !== null && optionValue !== '';
                              })
                              .map((option: any) => {
                                const optionValue = typeof option === 'string' ? option : option.value || option.label;
                                const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                                return { value: String(optionValue), label: String(optionLabel) };
                              });
                            return (
                              <Select
                                value={value2}
                                onChange={(e) => setValue2(e.target.value)}
                                placeholder="Select a value..."
                                name="value2"
                                options={value2SelectOpts}
                              />
                            );
                          }
                          default:
                            return (
                              <TextInput
                                type="text"
                                value={value2}
                                onChange={(e) => setValue2(e.target.value)}
                                placeholder="Enter value"
                                name="value2"
                              />
                            );
                        }
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* Scope */}
              <div>
                <Label className="mb-2">
                  Apply Formatting To
                </Label>
                <RadioGroup
                  options={[
                    { value: 'column', label: 'Selected Column Only' },
                    { value: 'row', label: 'Entire Row' }
                  ]}
                  value={scope}
                  onChange={(val) => setScope(val as 'column' | 'row')}
                  name="scope"
                  orientation="horizontal"
                />
              </div>

              {/* Style */}
              <div>
                <Label className="mb-2">
                  Style
                </Label>
                <div className="space-y-2">
                  <div>
                    <label className={cn('block text-xs font-medium mb-1', colors.textSecondary)}>Background Color</label>
                    <div className="flex flex-wrap gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color.bg}
                          type="button"
                          onClick={() => {
                            setBackgroundColor(color.bg);
                            setTextColor(color.text);
                          }}
                          className={cn(
                            'px-3 py-2 rounded text-xs border',
                            backgroundColor === color.bg ? 'ring-2 ring-indigo-500' : '',
                            color.bg,
                            color.text
                          )}
                        >
                          {color.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={cn('block text-xs font-medium mb-1', colors.textSecondary)}>Font Weight</label>
                    <Select
                      value={fontWeight}
                      onChange={(e) => setFontWeight(e.target.value)}
                      placeholder="Select font weight..."
                      name="fontWeight"
                      options={[
                        { value: '', label: 'Normal' },
                        { value: 'font-semibold', label: 'Semibold' },
                        { value: 'font-bold', label: 'Bold' },
                      ]}
                    />
                  </div>
                </div>
              </div>

              {/* Priority */}
              <div>
                <Label>
                  Priority (Higher = Applied First)
                </Label>
                <TextInput
                  type="number"
                  value={String(priority)}
                  onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                  placeholder="0"
                  name="priority"
                />
              </div>

              {/* Preview */}
              <div className={cn('p-3 rounded border', colors.surface, colors.border)}>
                <strong className={cn('text-sm', colors.text)}>Preview:</strong>
                <div className="mt-2">
                  <span
                    className={cn(
                      'inline-block px-3 py-2 rounded',
                      backgroundColor,
                      textColor,
                      fontWeight
                    )}
                  >
                    {conditionMode === 'column' 
                      ? `${selectedColumn || 'column'} ${operatorMap[operator]} ${value || '?'}`
                      : `${expression || 'expression'} ${operatorMap[operator]} ${value || '?'}`
                    }
                  </span>
                </div>
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowSaveModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRule} disabled={isLoading || !ruleName.trim()}>
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && ruleToDelete && (
        <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} size="sm">
          <ModalHeader>
            <ModalTitle>Delete Conditional Formatting Rule</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <p className={cn('text-sm', colors.text)}>
                Are you sure you want to delete the rule &quot;{ruleToDelete.name}&quot;?
              </p>
              <p className={cn('text-xs', colors.textMuted)}>This action cannot be undone.</p>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteRule} disabled={isLoading}>
              Delete Rule
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default ConditionalFormattingManager;

